import { ErrorHandler, Injectable } from '@angular/core';

/**
 * Global error handler that detects lazy-chunk
 * loading failures (typical after a new deploy
 * while the user has a stale index.html in memory)
 * and reloads the page ONCE to recover.
 *
 * Uses sessionStorage with a timestamp to avoid
 * infinite reload loops: if a reload was already
 * attempted recently, we give up and log the error
 * instead of reloading again. After the cooldown
 * window, a new reload is allowed so a second
 * deploy in the same session can also recover.
 */
@Injectable()
export class ChunkErrorHandler
  implements ErrorHandler {
  private static readonly STORAGE_KEY =
    'chunk-reload-last-attempt';
  private static readonly COOLDOWN_MS =
    60 * 1000;
  /**
   * Webpack throws `ChunkLoadError` ("Loading chunk
   * X failed"). The esbuild builder uses native
   * `import()`, whose message depends on the browser:
   * Chrome/Edge "Failed to fetch dynamically imported
   * module", Firefox "error loading dynamically
   * imported module", Safari "Importing a module
   * script failed".
   */
  private static readonly CHUNK_ERROR_PATTERNS:
    readonly RegExp[] = [
      /ChunkLoadError/i,
      /Loading chunk [\w-]+ failed/i,
      /dynamically imported module/i,
      /Importing a module script failed/i,
    ];

  handleError(error: unknown): void {
    if (this.isChunkLoadError(error)) {
      this.tryReload(error);
      return;
    }
    console.error(error);
  }

  private isChunkLoadError(
    error: unknown
  ): boolean {
    const err = error as {
      name?: string; message?: string;
    } | null;
    if (!err) return false;
    if (err.name === 'ChunkLoadError') return true;
    const message = err.message ?? '';
    return ChunkErrorHandler.CHUNK_ERROR_PATTERNS
      .some((pattern) => pattern.test(message));
  }

  private tryReload(error: unknown): void {
    const now = Date.now();
    const lastAttempt = this.readLastAttempt();
    const withinCooldown = lastAttempt !== null
      && (now - lastAttempt)
        < ChunkErrorHandler.COOLDOWN_MS;

    if (withinCooldown) {
      console.error(
        'ChunkLoadError detected but reload was '
        + 'already attempted recently. Skipping '
        + 'reload to avoid a loop.',
        error
      );
      return;
    }

    this.writeLastAttempt(now);
    this.reloadPage();
  }

  /** Hard reload, isolated so specs can intercept it. */
  protected reloadPage(): void {
    window.location.reload();
  }

  private readLastAttempt(): number | null {
    try {
      const raw = sessionStorage.getItem(
        ChunkErrorHandler.STORAGE_KEY
      );
      if (!raw) return null;
      const parsed = Number(raw);
      return Number.isFinite(parsed)
        ? parsed : null;
    } catch {
      return null;
    }
  }

  private writeLastAttempt(
    timestamp: number
  ): void {
    try {
      sessionStorage.setItem(
        ChunkErrorHandler.STORAGE_KEY,
        String(timestamp)
      );
    } catch {
      /* sessionStorage unavailable - ignore */
    }
  }
}
